<template>
  <div class="max-w-5xl mx-auto mt-8 bg-white p-6 rounded-xl shadow-md">
    <h2 class="text-2xl font-bold mb-6 flex items-center gap-2">
      Edit Educational Goals
      <BaseInfoIcon :text="ERKLAERUNG.lernziele" label="Edit Educational Goals" />
    </h2>

    <!-- Session Selection -->
    <div class="mb-6">
      <label class="block mb-2 font-semibold">Select Session</label>
      <BaseSelect
        v-model="selectedSessionId"
        :options="flattenedSessions"
        placeholder="-- Select Session --"
      />
    </div>

    <!-- No session selected -->
    <div
      v-if="!selectedSessionId"
      class="text-gray-500 text-center p-6 bg-gray-50 rounded"
    >
      Please select a session to edit educational goals.
    </div>

    <!-- Users table -->
    <div v-else>
      <table class="w-full border-collapse">
        <thead>
          <tr class="bg-gray-100">
            <th class="text-left p-3 border">User</th>
            <th class="text-left p-3 border">Educational Goal</th>
            <th class="p-3 border text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="user in users"
            :key="user.id"
            class="hover:bg-gray-50 transition"
          >
            <td class="p-3 border font-medium">
              {{ user.first_name }} {{ user.last_name }}
            </td>

            <td class="p-3 border">
              <textarea
                v-model="userGoals[user.id]"
                rows="2"
                class="w-full border rounded p-2"
                placeholder="Enter educational goal..."
              ></textarea>
            </td>

            <td class="p-3 border text-center">
              <BaseButton
                variant="save"
                size="sm"
                @click="saveGoal(user.id)"
                :disabled="savingUserId === user.id"
              >
                {{ savingUserId === user.id ? "Saving..." : "Save" }}
              </BaseButton>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="errorMessage" class="mt-4 text-red-600 text-center">
        {{ errorMessage }}
      </div>

      <div v-if="successMessage" class="mt-4 text-green-600 text-center">
        {{ successMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import BaseSelect from "@/BaseComponents/BaseSelect.vue";
import BaseButton from "@/BaseComponents/BaseButton.vue";
import BaseInfoIcon from "@/BaseComponents/BaseInfoIcon.vue";
import { ERKLAERUNG } from "@/async-sessions/erklaerungen";
import { getStudentUsers } from "@/api/users";
import { getLiveSessions } from "@/live-sessions/api/sessions";
import { getUserSession, updateUserSession } from "@/live-sessions/api/usersessions";

export default {
  components: { BaseSelect, BaseButton, BaseInfoIcon },
  data() {
    return {
      ERKLAERUNG,
      sessions: [],
      users: [],
      selectedSessionId: "",
      userGoals: {}, // { userId: goalText }
      savingUserId: null,
      successMessage: "",
      errorMessage: "",
    };
  },
  computed: {
    flattenedSessions() {
      const flatten = (sessions, depth = 0) =>
        sessions.flatMap((s) => [
          { value: s.id.toString(), label: `${"— ".repeat(depth)}${s.title}` },
          ...(s.children?.length ? flatten(s.children, depth + 1) : []),
        ]);
      return flatten(this.sessions);
    },
  },
  watch: {
    selectedSessionId: "loadUserGoals",
  },
  methods: {
    async loadSessions() {
      try {
        const res = await getLiveSessions();
        this.sessions = res.data;
      } catch (err) {
        this.sessions = [];
      }
    },
    async loadUsers() {
      try {
        const res = await getStudentUsers();
        this.users = res.data;
      } catch (err) {
        this.users = [];
      }
    },
    async loadUserGoals() {
      if (!this.selectedSessionId) {
        this.userGoals = {};
        return;
      }
      this.successMessage = "";
      this.errorMessage = "";

      const goals = {};
      for (const user of this.users) {
        try {
          const res = await getUserSession(user.id, this.selectedSessionId);
          goals[user.id] = res.data?.goal || "";
        } catch {
          goals[user.id] = "";
        }
      }
      this.userGoals = goals;
    },
    async saveGoal(userId) {
      if (!this.selectedSessionId) return;
      this.savingUserId = userId;
      this.successMessage = "";
      this.errorMessage = "";
      try {
        await updateUserSession(userId, this.selectedSessionId, {
          goal: this.userGoals[userId],
        });
        this.successMessage = `Saved goal for ${this.getUserName(userId)}`;
      } catch (err) {
        this.errorMessage = `Failed to save goal for ${this.getUserName(userId)}`;
        console.error(err);
      } finally {
        this.savingUserId = null;
        setTimeout(() => (this.successMessage = ""), 3000);
      }
    },
    getUserName(userId) {
      const user = this.users.find((u) => u.id === userId);
      return user ? `${user.first_name} ${user.last_name}` : "User";
    },
  },
  async mounted() {
    await Promise.all([this.loadSessions(), this.loadUsers()]);
  },
};
</script>

<style scoped>
textarea {
  resize: vertical;
}
</style>
